import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  SimpleGrid,
  Badge,
  Divider,
  useBreakpointValue,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Course Pricing Section
 * Displays price, value breakdown, EMI options, guarantee, and enroll CTA
 */
const CoursePricing = ({ data, startDate, courseId }) => {
  // Responsive layout (hooks must be called before any early returns)
  const gridCols = useBreakpointValue({ base: 1, md: 2 });

  // Modal states
  const { isOpen: isValueBreakdownOpen, onOpen: onValueBreakdownOpen, onClose: onValueBreakdownClose } = useDisclosure();
  const { isOpen: isEmailGateOpen, onOpen: onEmailGateOpen, onClose: onEmailGateClose } = useDisclosure();

  // Email gate disabled - show price by default
  const [hasEmail, setHasEmail] = useState(true);


  if (!data) return null;

  const {
    currency = 'INR',
    mrp,
    price,
    badge: priceBadge,
    discountText,
    emi,
    guarantee,
    valueBreakdown = [],
    totalValue,
    includesList = [],
    enrollmentUrl: enrollmentUrlTemplate
  } = data;

  // Replace {courseId} placeholder with actual courseId
  const enrollmentUrl = enrollmentUrlTemplate?.replace('{courseId}', courseId) || '/enroll';

  // Calculate discount percentage
  const discountPercent = mrp && price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  // Currency symbols
  const currencySymbols = {
    INR: '₹',
    USD: '$',
    EUR: '€'
  };
  const currencySymbol = currencySymbols[currency] || currency;

  return (
    <Box py={{ base: 16, md: 20 }} bg="gray.50" borderTop="1px solid" borderColor="gray.200" data-pricing>
      <Container maxW="container.lg">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <SimpleGrid columns={gridCols} spacing={{ base: 8, md: 12 }} alignItems="start">
            {/* LEFT: What's Included */}
            <VStack align="start" spacing={6}>
              <VStack align="start" spacing={2}>
                <Heading as="h2" size="2xl" fontWeight="900" color="gray.900">
                  What You Get
                </Heading>
                <Text fontSize="md" color="gray.600">
                  Everything you need to master this course
                </Text>
              </VStack>

              {/* Includes List */}
              {includesList.length > 0 && (
                <List spacing={3} w="full">
                  {includesList.slice(0, 8).map((item, idx) => (
                    <ListItem key={idx} display="flex" alignItems="flex-start">
                      <ListIcon as={CheckIcon} color="brand.600" mt="2px" flexShrink={0} />
                      <Text fontSize="sm" color="gray.700" ml={2}>
                        {item}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Show Value Details Button */}
              {valueBreakdown.length > 0 && (
                <Button
                  onClick={onValueBreakdownOpen}
                  variant="outline"
                  colorScheme="brand"
                  w="full"
                  mt={6}
                  fontWeight="700"
                >
                  Show Value Details
                </Button>
              )}
            </VStack>

            {/* RIGHT: Pricing Card */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Box
                bg="white"
                borderRadius="xl"
                p={8}
                boxShadow="0 10px 40px rgba(0,0,0,0.1)"
                position="sticky"
                top={20}
              >
                <VStack spacing={6} w="full">
                  {/* Discount Badge */}
                  {priceBadge && (
                    <Badge
                      colorScheme="green"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="700"
                    >
                      {priceBadge}
                    </Badge>
                  )}

                  {/* Pricing */}
                  <VStack spacing={2} w="full">
                    {mrp && mrp > price && (
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.600">
                          Original Price
                        </Text>
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          textDecoration="line-through"
                        >
                          {currencySymbol}{mrp}
                        </Text>
                      </HStack>
                    )}

                    <HStack
                      justify="space-between"
                      w="full"
                      onClick={() => !hasEmail && onEmailGateOpen()}
                      cursor={!hasEmail ? 'pointer' : 'default'}
                      transition="all 0.2s"
                      _hover={!hasEmail ? { opacity: 0.8 } : {}}
                    >
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        Course Price
                      </Text>
                      {!hasEmail ? (
                        <Box
                          px={4}
                          py={2}
                          bg="gray.100"
                          borderRadius="md"
                          filter="blur(6px)"
                          userSelect="none"
                          textAlign="center"
                          minW="100px"
                        >
                          <Text fontSize="sm" color="gray.400">••••••</Text>
                        </Box>
                      ) : (
                        <Heading as="span" size="2xl" color="brand.600">
                          {currencySymbol}{price}
                        </Heading>
                      )}
                    </HStack>

                    {hasEmail && discountPercent > 0 && (
                      <Text fontSize="xs" color="green.600" fontWeight="600">
                        Save {currencySymbol}{mrp - price} ({discountPercent}% off)
                      </Text>
                    )}
                  </VStack>

                  {hasEmail && <Divider />}

                  {/* EMI Option */}
                  {hasEmail && emi?.available && (
                    <Box w="full" p={3} bg="brand.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Or pay in {emi.months} installments
                      </Text>
                      <Text fontSize="lg" fontWeight="700" color="brand.600">
                        {currencySymbol}{emi.amount} × {emi.months}
                      </Text>
                    </Box>
                  )}

                  {/* Guarantee */}
                  {hasEmail && guarantee && (
                    <Box w="full" p={3} bg="yellow.50" borderRadius="md" borderLeft="4px" borderColor="yellow.400">
                      <Text fontSize="xs" fontWeight="600" color="gray.900">
                        🛡️ {guarantee}
                      </Text>
                    </Box>
                  )}

                  {/* Start Date */}
                  {startDate && (
                    <Box w="full" p={3} bg="blue.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600">
                        Course Starts
                      </Text>
                      <Text fontSize="sm" fontWeight="700" color="blue.700">
                        📅 {new Date(startDate).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Text>
                    </Box>
                  )}

                  {/* Enroll Button */}
                  {hasEmail ? (
                    <Button
                      as="a"
                      href={enrollmentUrl}
                      colorScheme="brand"
                      size="lg"
                      fontWeight="700"
                      w="full"
                      h="56px"
                      fontSize="md"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      transition="all 0.2s"
                    >
                      Enroll Now
                    </Button>
                  ) : (
                    <Button
                      onClick={onEmailGateOpen}
                      colorScheme="brand"
                      size="lg"
                      fontWeight="700"
                      w="full"
                      h="56px"
                      fontSize="md"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      transition="all 0.2s"
                    >
                      View Pricing & Enroll
                    </Button>
                  )}

                  {/* Secondary text */}
                  {discountText && (
                    <Text fontSize="xs" color="gray.600" textAlign="center">
                      {discountText}
                    </Text>
                  )}
                </VStack>
              </Box>
            </MotionBox>
          </SimpleGrid>
        </MotionBox>
      </Container>

      {/* Email Gate Modal (shown on page load if no email captured) */}
      {!hasEmail && (
        <Modal isOpen={isEmailGateOpen} onClose={onEmailGateClose} size="md" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Unlock Course Pricing</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} py={6}>
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Enter your email to view course pricing and secure your spot
                </Text>
                <Box
                  as="form"
                  w="full"
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <VStack spacing={3} w="full">
                    <input
                      id="email-gate-input"
                      type="email"
                      placeholder="you@example.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e0',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                      required
                    />
                  </VStack>
                </Box>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  We respect your privacy. Unsubscribe at any time.
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter pt={0}>
              <Button
                colorScheme="brand"
                w="full"
                size="md"
                onClick={() => {
                  const emailInput = document.getElementById('email-gate-input');
                  if (emailInput?.value) {
                    localStorage.setItem('userEmail', emailInput.value);
                    setHasEmail(true);
                    onEmailGateClose();
                  }
                }}
              >
                View Pricing
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Value Breakdown Modal */}
      <Modal isOpen={isValueBreakdownOpen} onClose={onValueBreakdownClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Value Breakdown</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              {valueBreakdown.map((item, idx) => (
                <Box
                  key={idx}
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor="brand.600"
                >
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="600" color="gray.900">
                      {item.item}
                    </Text>
                    <Text fontSize="sm" fontWeight="700" color="brand.600">
                      {currencySymbol}{item.value}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.600">
                    {item.description}
                  </Text>
                </Box>
              ))}
              {totalValue && (
                <>
                  <Divider my={4} />
                  <HStack justify="space-between">
                    <Text fontWeight="700" color="gray.900">
                      Total Value
                    </Text>
                    <Text fontWeight="900" fontSize="lg" color="brand.600">
                      {currencySymbol}{totalValue}
                    </Text>
                  </HStack>
                </>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CoursePricing;
